import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import { REPLACEMENT_LEAVE_OPTIONS, getRecommendedReplacementLeaveByTime, calculateOvertimeHours } from "common/overtime";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppSelectInput from "components/app-select-input";

export const AppApproveOvertimeModal = (props, ref) => {
	const dispatch = useDispatch();
	const [record, setRecord] = useState({});
	const [visible, setVisible] = useState(false);
	const initialValues = useMemo(() => ({ replacementLeave: "" }), []);
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			replacementLeave: yup.string().required(ERRORS.REQUIRED),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const overtimeHours = useMemo(() => calculateOvertimeHours(record.startTime, record.endTime), [record.startTime, record.endTime]);

	const recommended = useMemo(() => getRecommendedReplacementLeaveByTime(record.startTime, record.endTime), [record.startTime, record.endTime]);

	const setFieldValue = useMemo(() => formik.setFieldValue, [formik]);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	const onHandleShow = useCallback((data) => {
		setRecord(data || {});
		setVisible(true);
	}, []);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;

		try {
			/* do not send status - the controller sets it */
			await api.post.overtime.managerApprove({ id: record.id, replacementLeave: parseFloat(values.replacementLeave) });
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: "Overtime has been approved successfully" }));
			props.onHandleGetList();
		}
	}, [dispatch, record.id, formik, onHandleDismiss, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	/* prefill with the recommendation whenever a new record is opened */
	useEffect(() => {
		if (!visible) return;

		setFieldValue("replacementLeave", recommended.toString());
	}, [visible, recommended, setFieldValue]);

	return (
		<Modal classes={{ root: "app-approve-overtime-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="approve-overtime-modal" aria-describedby="approve-overtime-modal">
			<div className="approve-overtime-modal">
				<div className="approve-overtime-modal__main">
					<div className="approve-overtime-modal__header">
						<p className="approve-overtime-modal__title">Approve Overtime</p>
						<p className="approve-overtime-modal__description">Confirm the replacement leave to be granted for this overtime request.</p>
					</div>

					<form className="approve-overtime-modal__body" onSubmit={formik.handleSubmit}>
						<AppInput type="text" label="Employee Name" value={record.employeeName || "-"} disabled />

						<AppInput type="text" label="Overtime Hours" value={overtimeHours ? overtimeHours.toString() : "-"} disabled />

						{/* prettier-ignore */}
						<AppSelectInput required searchable={false} type="text" name="replacementLeave" label="Replacement Leave (Days)" placeholder="Please Select" options={REPLACEMENT_LEAVE_OPTIONS} value={formik.values.replacementLeave} error={formik.errors.replacementLeave} touched={formik.touched.replacementLeave} disabled={formik.isSubmitting} onChange={formik.handleChange} />

						<div className="approve-overtime-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Confirm" disabled={formik.isSubmitting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppApproveOvertimeModal));

AppApproveOvertimeModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
};
